import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Mail, Send, ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export default function ExpertMessages() {
  const { user } = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "de" ? "de-DE" : i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR";

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const conversationFromUrl = params.get("conversation");

  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(conversationFromUrl || null);

  const [peopleById, setPeopleById] = useState({});
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);

  const subRef = useRef(null);
  const endRef = useRef(null);

  // Auto-scroll vers le bas quand le thread change ou qu'un nouveau message arrive
  useEffect(() => {
    try {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    } catch (_) {}
  }, [messages.length, activeId]);

  useEffect(() => {
    if (conversationFromUrl && conversationFromUrl !== activeId) setActiveId(conversationFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationFromUrl]);

  const otherUserId = (c) => {
    if (!user?.id) return null;
    return c.expert_id === user.id ? c.client_id : c.expert_id;
  };

  const displayName = (uid) => {
    const p = peopleById[uid];
    if (!p) return uid
      ? t("messaging.userWithId", { id: String(uid).slice(0, 8) })
      : t("messaging.user");
    const full = `${p.first_name || ""} ${p.last_name || ""}`.trim();
    return full || t("messaging.userWithId", { id: String(uid).slice(0, 8) });
  };

  const loadConversations = async () => {
    if (!user?.id) return;
    setLoadingList(true);
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("id, subject, expert_id, client_id, updated_at, created_at, context_type, context_id")
        .or(`expert_id.eq.${user.id},client_id.eq.${user.id}`)
        .order("updated_at", { ascending: false });

      if (error) throw error;
      const rows = Array.isArray(data) ? data : [];
      setConversations(rows);

      // Enrichir noms via RPC (PAS de select direct sur profiles)
      const ids = [...new Set(rows.map(otherUserId).filter(Boolean))];
      if (ids.length) {
        const { data: profs, error: perr } = await supabase.rpc("get_profile_basics", { p_user_ids: ids });
        if (!perr && Array.isArray(profs)) {
          setPeopleById((prev) => {
            const next = { ...prev };
            for (const p of profs) next[p.id] = p;
            return next;
          });
        }
      }

      // Auto-select si rien n’est choisi
      if (!activeId && rows[0]?.id) {
        const nextId = rows[0].id;
        setActiveId(nextId);
        const q = new URLSearchParams(location.search);
        q.set("conversation", nextId);
        navigate({ search: q.toString() }, { replace: true });
      }
    } catch (e) {
      console.error("[messages] loadConversations", e);
      toast.error(t("messaging.errors.loadConversations"));
    } finally {
      setLoadingList(false);
    }
  };

  const loadMessages = async (conversationId) => {
    if (!user?.id || !conversationId) return;
    setLoadingThread(true);
    try {
      const { data, error } = await supabase
        .from("messages")
        .select("id, conversation_id, sender_id, body, created_at")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true })
        .limit(300);

      if (error) throw error;
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("[messages] loadMessages", e);
      toast.error(t("messaging.errors.loadMessages"));
      setMessages([]);
    } finally {
      setLoadingThread(false);
    }
  };

  const subscribeToMessages = (conversationId) => {
    try {
      if (subRef.current) supabase.removeChannel(subRef.current);
    } catch (_) {}

    if (!conversationId) return;

    const ch = supabase
      .channel(`messages:${conversationId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${conversationId}` },
        (payload) => {
          const row = payload?.new;
          if (!row?.id) return;
          setMessages((prev) => (prev.some((m) => m.id === row.id) ? prev : [...prev, row]));
        }
      )
      .subscribe();

    subRef.current = ch;
  };

  useEffect(() => {
    loadConversations();
    // cleanup
    return () => {
      try {
        if (subRef.current) supabase.removeChannel(subRef.current);
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId);
    subscribeToMessages(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, user?.id]);

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeId) || null,
    [conversations, activeId]
  );

  const sendMessage = async () => {
    const text = (draft || "").trim();
    if (!text || !activeId || !user?.id) return;

    // ✅ Optimistic UI : afficher tout de suite le message (sinon l'expert pense que rien n'a été envoyé)
    const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const optimisticRow = {
      id: optimisticId,
      conversation_id: activeId,
      sender_id: user.id,
      body: text,
      created_at: new Date().toISOString(),
      __optimistic: true,
    };

    setDraft("");
    setMessages((prev) => [...prev, optimisticRow]);

    // Mettre à jour le tri côté liste (sans refresh)
    setConversations((prev) => {
      const next = prev.map((c) => (c.id === activeId ? { ...c, updated_at: new Date().toISOString() } : c));
      next.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      return next;
    });

    try {
      const { data, error } = await supabase
        .from("messages")
        .insert([
          {
            conversation_id: activeId,
            sender_id: user.id,
            body: text,
          },
        ])
        .select("id, conversation_id, sender_id, body, created_at")
        .single();
      if (error) throw error;

      // Remplacer l'optimistic par la row DB
      if (data?.id) {
        setMessages((prev) => prev.map((m) => (m.id === optimisticId ? data : m)));
      } else {
        await loadMessages(activeId);
      }
    } catch (e) {
      console.error("[messages] send", e);
      toast.error(t("messaging.errors.send"));
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setDraft(text);
    }
  };

  const pickConversation = (id) => {
    setActiveId(id);
    const q = new URLSearchParams(location.search);
    q.set("conversation", id);
    navigate({ search: q.toString() }, { replace: true });
  };

  return (
    <div style={{ minHeight: "100vh", background: "#fafaf8", padding: 24 }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              border: "1px solid #e5e3df",
              background: "#fff",
              borderRadius: 2,
              padding: "8px 10px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: "system-ui, sans-serif",
              fontSize: 13,
            }}
            title={t("messaging.back")}
          >
            <ArrowLeft size={16} />
            {t("messaging.back")}
          </button>

          <div style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 400, color: "#1a1a1a" }}>
            {t("messaging.title")}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "340px 1fr",
            gap: 16,
            alignItems: "stretch",
          }}
        >
          {/* LISTE */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e3df",
              borderRadius: 2,
              padding: 12,
              height: "72vh",
              overflow: "auto",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Mail size={16} />
              <div style={{ fontFamily: "system-ui, sans-serif", fontWeight: 600 }}>{t("messaging.conversations")}</div>
            </div>

            {loadingList && (
              <div style={{ padding: 12, color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>
                {t("messaging.loading")}
              </div>
            )}

            {!loadingList && conversations.length === 0 && (
              <div style={{ padding: 12, color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>
                {t("messaging.emptyConversations")}
              </div>
            )}

            <div style={{ display: "grid", gap: 10 }}>
              {conversations.map((c) => {
                const other = otherUserId(c);
                const title = (c.subject || "").trim() || displayName(other);
                const isActive = c.id === activeId;

                return (
                  <button
                    key={c.id}
                    onClick={() => pickConversation(c.id)}
                    style={{
                      textAlign: "left",
                      border: isActive ? "1px solid #111" : "1px solid #e5e3df",
                      background: isActive ? "#111" : "#fff",
                      color: isActive ? "#fff" : "#1a1a1a",
                      borderRadius: 2,
                      padding: 12,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontFamily: "system-ui, sans-serif", fontWeight: 700, fontSize: 13 }}>
                      {title}
                    </div>
                    <div
                      style={{
                        marginTop: 6,
                        fontFamily: "system-ui, sans-serif",
                        fontSize: 12,
                        opacity: 0.75,
                      }}
                    >
                      {displayName(other)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* THREAD */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e3df",
              borderRadius: 2,
              padding: 12,
              height: "72vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ padding: 10, borderBottom: "1px solid #f1f1ef" }}>
              <div style={{ fontFamily: "system-ui, sans-serif", fontWeight: 700 }}>
                {activeConversation ? (activeConversation.subject || t("messaging.conversation")) : t("messaging.selectConversation")}
              </div>
            </div>

            <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
              {loadingThread && (
                <div style={{ color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>
                  {t("messaging.loading")}
                </div>
              )}

              {!loadingThread && !activeId && (
                <div style={{ color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>
                  {t("messaging.chooseConversation")}
                </div>
              )}

              {!loadingThread && activeId && messages.length === 0 && (
                <div style={{ color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>
                  {t("messaging.emptyMessages")}
                </div>
              )}

              <div style={{ display: "grid", gap: 10 }}>
                {messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div
                      key={m.id}
                      style={{
                        display: "flex",
                        justifyContent: mine ? "flex-end" : "flex-start",
                      }}
                    >
                      <div
                        style={{
                          maxWidth: "70%",
                          padding: "10px 12px",
                          borderRadius: 2,
                          border: "1px solid #e5e3df",
                          background: mine ? "#111" : "#fafaf8",
                          color: mine ? "#fff" : "#1a1a1a",
                          fontFamily: "system-ui, sans-serif",
                          fontSize: 13,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {m.body}
                        <div style={{ marginTop: 6, opacity: 0.7, fontSize: 11 }}>
                          {new Date(m.created_at).toLocaleString(locale)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ancre pour auto-scroll */}
              <div ref={endRef} />
            </div>

            <div style={{ borderTop: "1px solid #f1f1ef", padding: 10, display: "flex", gap: 10 }}>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t("messaging.placeholder")}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: 2,
                  border: "1px solid #e5e3df",
                  fontFamily: "system-ui, sans-serif",
                  fontSize: 13,
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                disabled={!activeId}
              />
              <button
                onClick={sendMessage}
                disabled={!activeId || !(draft || "").trim()}
                style={{
                  padding: "10px 12px",
                  borderRadius: 2,
                  border: "1px solid #111",
                  background: "#111",
                  color: "#fff",
                  cursor: !activeId || !(draft || "").trim() ? "not-allowed" : "pointer",
                  fontFamily: "system-ui, sans-serif",
                  fontSize: 13,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  opacity: !activeId || !(draft || "").trim() ? 0.5 : 1,
                }}
              >
                <Send size={16} />
                {t("messaging.send")}
              </button>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 10, color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 12 }}>
          {t("messaging.rlsNote")}
        </div>
      </div>
    </div>
  );
}
